import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import type {
  ProposalDetailDto,
  ProposalDto,
  ProposalSiteRefDto,
  ProposalVersionDto,
} from '@lib/dto/proposal.dto'
import type { ProposalDocumentDto } from '@lib/dto/proposal-document.dto'
import type { ProposalWork } from '@lib/enums/proposal-work.enum'
import { proposalService } from '@/services/ProposalService'
import { proposalKeys } from '@/hooks/queryKeys'
import { useProposal } from '@/hooks/useProposal'
import { useProposalDocument } from '@/hooks/useProposalDocument'

/** Which survey screen is asking: each totals only its own kind of work. */
export type BidWork = 'trees' | 'irrigation'

/** A draft the survey is adding up, and this site's place in it. */
export interface Bid {
  proposal: ProposalDetailDto
  version: ProposalVersionDto
  site: ProposalSiteRefDto
}

export interface BidContext {
  /** Still finding out which bid, if any. */
  isResolving: boolean
  /** The draft being added up, or null to add up this site on its own. */
  bid: Bid | null
  /** Every open draft that includes this site, for switching between them. */
  drafts: ProposalDto[]
  /** Proposals could not be read, usually for want of a signal. */
  isUnavailable: boolean
  /** The draft's document, for the bid's other sites. */
  document: UseQueryResult<ProposalDocumentDto>
}

function bidsFor(work: ProposalWork, kind: BidWork): boolean {
  return kind === 'trees' ? work !== 'IRRIGATION' : work !== 'TREES'
}

/**
 * The draft a proposal offers this site's work in, or null when it does not:
 * its newest version has been sent, leaves the site out, or bids the other
 * kind of work.
 */
export function draftFor(
  proposal: ProposalDetailDto,
  siteId: string,
  kind: BidWork,
): Bid | null {
  if (proposal.archivedAt !== null || !bidsFor(proposal.work, kind)) return null
  const version = proposal.versions[0]
  if (version === undefined || !version.isEditable) return null
  const site = version.sites.find((entry) => entry.siteId === siteId)
  return site === undefined ? null : { proposal, version, site }
}

/**
 * Which bid the survey screen adds up.
 *
 * The proposal the technician came from, when it has a draft covering this
 * site; otherwise the most recently changed open draft that does; otherwise
 * none, and the screen adds up the site on its own (the operator's answers,
 * 2026-11-04). A proposal that cannot be read leaves the site on its own and
 * says so, rather than guessing.
 */
export function useBid(
  siteId: string,
  kind: BidWork,
  requestedId: string | null,
): BidContext {
  const list = useQuery({
    queryKey: proposalKeys.list({ siteId }),
    queryFn: () => proposalService.list({ siteId }),
    staleTime: 15_000,
    retry: false,
  })
  const drafts = (list.data?.proposals ?? []).filter(
    (proposal) =>
      proposal.archivedAt === null &&
      proposal.latestStatus === 'DRAFT' &&
      bidsFor(proposal.work, kind),
  )

  const requested = useProposal(requestedId ?? '')
  const requestedBid =
    requested.data === undefined
      ? null
      : draftFor(requested.data, siteId, kind)
  /*
   * The requested proposal wins once it is known to have a draft here. Until
   * then, and when it has none, the newest open draft stands in.
   */
  const fallbackId =
    drafts.find((proposal) => proposal.id !== requestedId)?.id ?? ''
  const useFallback =
    requestedId === null || (requested.data !== undefined && requestedBid === null)
  const fallback = useProposal(useFallback ? fallbackId : '')
  const fallbackBid =
    fallback.data === undefined ? null : draftFor(fallback.data, siteId, kind)

  const bid = requestedBid ?? (useFallback ? fallbackBid : null)
  const document = useProposalDocument(bid?.version.id ?? '')

  const isResolving =
    (requestedId !== null && requested.isPending && !requested.isError) ||
    (useFallback && list.isPending && !list.isError) ||
    (useFallback && fallbackId !== '' && fallback.isPending && !fallback.isError)

  return {
    isResolving,
    bid,
    drafts,
    isUnavailable:
      bid === null && (list.isError || requested.isError || fallback.isError),
    document,
  }
}
